import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  kycDocumentsService,
  KycRequirementsResponseDto,
  KycRequirementSlotDto,
  KycSmartDocumentDto,
  UploadRequest,
} from '../../../../../services/kycDocuments.service';
import { investorService } from '../../../../../services/investor.service';
import { getSAProxyInvestorId } from '../../../../../services/saProxyAdapter';
import { toServiceAgentPath } from '../../../../../utils/journeySteps';
import { MyDocumentsSection } from './MyDocumentsSection';
import { RequirementCard } from './RequirementCard';
import { UploadDialog } from './UploadDialog';
import { ExtractedFieldsReview } from './ExtractedFieldsReview';
import { ExpiryNotice } from './ExpiryNotice';
import { blockingAlertText } from './kycDiscrepancy';
import AlertDialog from '../../../../../components/AlertDialog/AlertDialog';
import './SmartUpload.scss';

interface KycDocumentsPageProps {
  /** Journey context: scopes requirements + uploads to the journey's person. Null = login person. */
  journeyId?: string | null;
  /** R2: "Upload all fresh" — the investor chose to replace their vault documents. */
  freshMode?: boolean;
  /** Journey-less page: still render the uploader (login person's requirements). */
  uploadMode?: boolean;
}

export const KycDocumentsPage: React.FC<KycDocumentsPageProps> = ({ journeyId = null, freshMode = false, uploadMode = false }) => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState<KycRequirementsResponseDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSlot, setActiveSlot] = useState<KycRequirementSlotDto | null>(null);
  const [reviewDoc, setReviewDoc] = useState<KycSmartDocumentDto | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    if (!journeyId && !uploadMode) {
      // Journey-less Documents Center renders only the document manager — no slot
      // grid, so no requirements fetch. mode=upload keeps the uploader (login person).
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const data = await kycDocumentsService.getRequirements(journeyId);
      setRequirements(data);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load requirements';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [journeyId, uploadMode]);

  useEffect(() => {
    load();
  }, [load]);

  const progressText = useMemo(() => {
    if (!requirements) return '';
    return `${requirements.mandatoryComplete} of ${requirements.mandatoryTotal} required documents uploaded`;
  }, [requirements]);

  // Journey-context exits (UX F-10b/F-12, user skip model 2026-08-21):
  //  - "Skip remaining" only after >=1 upload; "Skip all" always; "Continue" when complete.
  // All land in the journey. P5 additionally records the server-side skip so the gate
  // resumes into the journey on the next login.
  const requiredComplete = !!requirements
    && requirements.mandatoryTotal > 0
    && requirements.mandatoryComplete >= requirements.mandatoryTotal;
  // F-12: completing the last required document lands DIRECTLY in the journey. Only on the
  // incomplete→complete TRANSITION while on the page (arriving already complete shows the
  // Continue button instead), and never while a dialog is open mid-confirm.
  // Consent-at-accept (2026-08-25): the R2 "apply to journey?" popup is gone — each accept
  // already recorded the consent and archived, so completion just lands in the journey.
  // UAT 20: under the SA proxy the "journey" is the agent's Information tabs for this
  // investor; for the investor it is their journey page, as before.
  const journeyPath = (jid: string) => {
    const saInvestorId = getSAProxyInvestorId();
    const target = `/investor/journey/${jid}`;
    return saInvestorId !== null ? toServiceAgentPath(target, saInvestorId) : target;
  };

  const wasIncompleteRef = useRef(false);
  useEffect(() => {
    if (!journeyId || !requirements) return;
    if (!requiredComplete) {
      wasIncompleteRef.current = true;
      return;
    }
    if (wasIncompleteRef.current && !activeSlot && !reviewDoc && !alert) {
      wasIncompleteRef.current = false;
      navigate(journeyPath(journeyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredComplete, requirements, activeSlot, reviewDoc, alert, journeyId]);

  const goToJourney = (recordSkip: boolean) => {
    if (!journeyId) return;
    if (recordSkip) {
      // Best-effort: the skip flag drives resume (F-11); a failure must not block entry.
      investorService.skipSmartUpload(journeyId)
        .catch((err) => console.error('[SmartUpload] skip record failed', err));
    }
    navigate(journeyPath(journeyId));
  };

  const handleUpload = async (req: UploadRequest, supersedesId?: number) => {
    setSubmitting(true);
    try {
      const scoped = { ...req, journeyId };
      const doc = supersedesId
        ? await kycDocumentsService.reUpload(supersedesId, scoped)
        : await kycDocumentsService.uploadDocument(scoped);
      setActiveSlot(null);
      setReviewDoc(doc);
      toast.success('Document uploaded');
      await load();
    } catch (err: any) {
      if (err?.response?.status === 409 && err?.response?.data) {
        setActiveSlot(null);
        const doc = err.response.data as KycSmartDocumentDto;
        const blockingText = blockingAlertText(doc);
        if (blockingText) {
          // Rejected (mismatch / random / unreadable / unverifiable) - surface the
          // specific reason in the shared alert popup, per design.
          setAlert({ title: 'Document not accepted', message: blockingText });
        } else {
          // 409 without a blocking reason: fall back to the review modal.
          setReviewDoc(doc);
          toast.warning('Upload needs review - please check the flagged fields');
        }
        await load();
      } else {
        const message = err?.response?.data?.message || err?.message || 'Upload failed';
        setAlert({ title: 'Upload failed', message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Journey-less Documents Center = the document manager only (view/manage what's
  // uploaded). The Smart Upload slot grid lives in journey context — or journey-less
  // with mode=upload (delegation KYC gate). (After all hooks — rules-of-hooks.)
  if (!journeyId && !uploadMode) {
    return (
      <div className="smart-upload">
        <MyDocumentsSection />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="smart-upload">
        <div className="text-center py-5 text-muted">Loading your KYC requirements...</div>
      </div>
    );
  }

  if (error || !requirements) {
    return (
      <div className="smart-upload">
        <div className="alert alert-danger">{error || 'Unable to load requirements'}</div>
        <button className="btn btn-outline-primary btn-sm" onClick={() => load()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="smart-upload">
      {/* D2: journey-less mode = the My Documents manager (filters + table) above the uploader. */}
      {!journeyId && <MyDocumentsSection />}
      <div className="smart-upload__header">
        <div>
          <h5 className="mb-1">Smart Upload</h5>
          <p className="text-muted mb-0 small">
            Upload your KYC documents. We extract key fields automatically via OCR and validate them
            against your PAN and registration details.
          </p>
        </div>
        <div className="smart-upload__progress">
          <span className="badge bg-light text-dark">{progressText}</span>
          {requirements.expiredCount > 0 && (
            <span className="badge bg-danger ms-2">
              {requirements.expiredCount} expired
            </span>
          )}
          {requirements.kycVerifiedAt && (
            <span className="badge bg-success ms-2">KYC Verified</span>
          )}
        </div>
      </div>

      {journeyId && freshMode && (
        <div className="alert alert-info py-2 small mb-3">
          Fresh upload mode — new versions you upload will replace your existing documents.
        </div>
      )}

      {journeyId && (
        <div className="d-flex align-items-center gap-2 mb-3">
          {requiredComplete ? (
            <button type="button" className="btn btn-success btn-sm" onClick={() => goToJourney(true)}>
              Continue to journey
            </button>
          ) : (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => goToJourney(true)}>
              Skip &amp; go to journey
            </button>
          )}
        </div>
      )}

      <ExpiryNotice slots={requirements.slots} />

      <div className="smart-upload__grid">
        {requirements.slots.map((slot) => (
          <RequirementCard
            key={slot.documentType}
            slot={slot}
            onUpload={() => setActiveSlot(slot)}
            onReview={async () => {
              if (!slot.currentDocumentId) return;
              try {
                const detail = await kycDocumentsService.detail(slot.currentDocumentId);
                setReviewDoc(detail);
              } catch (err: any) {
                toast.error(err?.message || 'Failed to load document detail');
              }
            }}
          />
        ))}
      </div>

      {activeSlot && (
        <UploadDialog
          slot={activeSlot}
          investorType={requirements.investorType}
          submitting={submitting}
          onCancel={() => setActiveSlot(null)}
          onSubmit={(req) => handleUpload(req, activeSlot.currentDocumentId ?? undefined)}
        />
      )}

      {reviewDoc && (
        <ExtractedFieldsReview
          doc={reviewDoc}
          onClose={() => setReviewDoc(null)}
          onConfirm={async (id, corrections) => {
            try {
              await kycDocumentsService.confirm(id, corrections, journeyId);
              // Auto-close on success (user 2026-08-26): the accept completed — the card's
              // updated state tells the story; keeping the modal open forced a manual close.
              // On error it stays open so the investor can retry or correct.
              setReviewDoc(null);
              toast.success(journeyId
                ? 'Document accepted — it will be submitted for this application'
                : 'Document confirmed - your details will be filled in when you open a journey and agree');
              await load();
            } catch (err: any) {
              const message = err?.response?.data?.message || err?.message || 'Confirm failed';
              toast.error(message);
            }
          }}
        />
      )}

      <AlertDialog
        show={!!alert}
        title={alert?.title}
        message={alert?.message ?? ''}
        onClose={() => setAlert(null)}
      />

    </div>
  );
};

export default KycDocumentsPage;
